import { SmartSendItemState } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { Feature } from '~/apps/smartsend/lib/types/Feature';

export declare type CacheItem<T> = {
  expiry: Date;
  added: Date;
  item: T;
};

export interface GetItemSnapshotsResponse {
  Items: SmartSendItemSnapshotApiModel[];
}

export interface ExportItemsResponse {
  Records: string[][];
  Header: string[];
}

export interface GetCollectionDatesResponse {
  CollectionDates: GetCollectionDatesApiModel[];
}

export interface GetCollectionDatesApiModel {
  CollectionDate: Date;
}

export interface GetCsvMappingsResponse {
  CsvMappings: CsvMappingApiModel[];
}

export interface CreateCsvMappingResponse {
  CsvMapping: CsvMappingApiModel;
}

export interface UpdateCsvMappingResponse {
  Id: number;
}
export interface DeleteCsvMappingResponse {}

export interface CsvMappingApiModel {
  Id?: number;
  Name: string;
  Description: string;
  Tags: string[];
  Mappings: CsvMappingItemApiModel[];
}

export interface CsvMappingItemApiModel {
  Id: string;
  To: string;
  From: string;
}

export interface GetRulesResponse {
  Rules: RuleApiModel[];
}

export interface CreateRuleResponse {
  Rule: RuleApiModel;
}

export interface UpdateRuleResponse {}

export interface DeleteRuleResponse {}

export interface RuleApiModel {
  Id?: number;
  Name: string;
  Description: string;
  Priority: number;
  ConditionType: string;
  RuleType: string;
  Actions: RuleActionApiModel[];
  Conditions: RuleConditionApiModel[];
  Tags: string[];
  Active?: boolean;
  BreakOnConditionMet?: boolean;
}

export interface RuleActionApiModel {
  Id: string;
  Field: string;
  Type: string;
  Value: string;
}

export interface RuleConditionApiModel {
  Id: string;
  Field: string;
  Comparison: string;
  Values: string[];
}

export interface GetItemByTrackingResponse {
  Item: SmartSendItemApiModel;
}

export interface GetBookedItemsResponse {
  Items: SmartSendItemApiModel[];
  Take: number;
  Skip: number;
}

export interface GetAwaitingDispatchItemsResponse {
  Items: SmartSendItemApiModel[];
  Take: number;
  Skip: number;
}

export interface GetInTransitItemsResponse {
  Items: SmartSendItemApiModel[];
  Take: number;
  Skip: number;
}

export interface GetAwaitingDispatchCountResponse {
  Count: number;
  To?: Date;
  From?: Date;
}
export interface GetInTransitCountResponse {
  Count: number;
  To?: Date;
  From?: Date;
}

export interface GetBookedCountResponse {
  Count: number;
  To?: Date;
  From?: Date;
}

export interface GetItemsResponse {
  Items: SmartSendItemApiModel[];
}

export interface SmartSendItemSnapshotApiModel {
  Id: string;
  Status: string;
}

export interface TriggerAllResponse {}

export interface GetOrdersResponse {
  Orders: SmartSendItemApiModel[];
  Take: number;
  Skip: number;
}

export interface GetManifestsResponse {
  Manifests: ManifestApiModel[];
}

export interface CreateManifestResponse {
  Manifest: ManifestApiModel;
}
export interface DeleteManifestResponse {}
export interface UpdateManifestResponse {
  Manifest: ManifestApiModel;
}
export interface ReallocateManifestResponse {}

export interface GetManifestResponse {
  Manifest: ManifestApiModel;
}
export interface GetManifestDownloadResponse {
  Base64: string;
}

export interface ManifestApiModel {
  Id: string;
  ManifestId: string;
  Status: ManifestStatusType;
  ForDate: string;
  HasDownload: boolean;
  Expires?: Date;
  ManifestBy?: TimeOneModel;
  ManifestOrderLines: ManifestOrderLineApiMode[];
}

export interface TimeOneModel {
  Hour: number;
  Minute: number;
}

export interface UpdateManifestApiModel {
  TriggerSubmission: boolean;
  Status?: ManifestStatusType;
  ManifestOrderLinesToAdd: ManifestOrderLineApiMode[];
  ManifestOrderLinesToRemove: ManifestOrderLineApiMode[];
}

export type ManifestStatusType =
  'DRAFT' | 'PROCESSING' | 'MANIFEST_COMPLETE' | 'PROCESSING_ERROR' | 'CLEAN_SWEEP' | 'EXTERNALLY_CLOSED';

export interface ManifestOrderLineApiMode {
  CourierConsignmentId: string;
  OrderLineId: string;
  SmartSendId: string;
  IdsToManifest: string[];
}

export interface GetClaimsResponse {
  Claims: CaseApiModel[];
}

export interface GetClaimResponse {
  Claim: CaseDetailsApiModel;
  Actions: CaseActionApiModel[];
}

export interface GetProductsResponse {
  Products: ProductApiModel[];
}

export interface ProductApiModel {
  Id: string;
  Dimensions?: Dimensions;
  ProductLink?: string;
  Description?: string;
  Sku?: string;
  ImageUrl?: string;
  TariffCode?: string;
  CountryOfManufacture?: string;
  Value?: number;
  Meta: Meta;
}

export interface TariffSearchApiModel {
  Hierarchy: TariffChapterApiModel[];
  Classifications: TariffClassificationApiModel[];
}

export interface BookingStatusRecordApiModel {
  ItemId: string;
  Booked: boolean;
  TrackingNumber: string;
}

export interface GetBookedStatusResponse {
  Records: BookingStatusRecordApiModel[];
}

export interface TariffSectionApiModel {
  Id: string;
  Name: string;
  FriendlyName: string;
  Chapters: TariffChapterApiModel[];
}

export interface TariffChapterApiModel {
  Id: string;
  Name: string;
  FriendlyName: string;
  Code: string;
  SectionId: string;
  Classifications: TariffClassificationApiModel[];
}

export interface TariffClassificationApiModel {
  Id: string;
  Description: string;
  FriendlyDescription: string;
  Code: string;
  ParentCode: string;
  SectionId: string;
  Selectable: boolean;
  Classifications: TariffClassificationApiModel[];
}

export interface GetSupportCasesResponse {
  Cases: CaseApiModel[];
}

export interface CaseApiModel {
  Provider: string;
  AccountId: string;
  Id: string;
  CustomerIdentifier: string;
  CustomerName: string;
  ReferenceNumber: string;
  InternalId: string;
  SubType: string;
  Link: string;
  Value: number;
  Created?: Date;
  LastUpdated?: Date;
  TimeInStatus: any;
  StatusTimeToLive?: Date;
  JoinedCurrentStatus: Date;
  Status: ClaimStatusApiModel;
  Type: ClaimTypeApiModel;
}

export interface CaseActionApiModel {
  Id: number;
  Name: string;
  Description: string;
  Requirements: CaseRequirementApiModel[];
  Origin: CaseTypeStatusApiModel;
  Destination: CaseTypeStatusApiModel;
  Note: string;
  CompleteMessage: string;
  Order: number;
  IsDefault: boolean;
}
export interface CaseHistoryApiModel {
  Id: number;
  OldClaimStatus: CaseTypeStatusApiModel;
  NewClaimStatus: CaseTypeStatusApiModel;
  Action: CaseActionApiModel;
  Timestamp: Date;
  User: string;
}

export interface CaseCommunicationApiModel {
  Id: number;
  Type: string;
  Content: string;
  Created: Date;
  From: string;
  To: string;
  Unread: boolean;
  CreatedBy: string;
}

export interface CaseNoteApiModel {
  Id: number;
  Note: string;
  CreatedDate: Date;
  CreatedBy: string;
}

export interface CaseMetaDataApiModel {
  Id: number;
  Name: string;
  Value: string;
  Type: string;
  CreatedDate: Date;
}

export interface CaseTypeStatusApiModel {
  Id: number;
  Name: string;
}

export interface CaseApplicationTypeApiModel {
  Id: number;
  Name: string;
}

export interface CaseRequirementValidationApiModel {
  ValidatorType: string;
  Value: string;
}

export interface CaseRequirementApiModel {
  Id: number;
  Name: string;
  Value: string;
  RequirementType: string;
  Readonly: boolean;
  EmailTemplateFieldName: string;
  Options: string[];
  Group: string;
  Description: string;
  Placeholder: string;
  Indexed: boolean;
  Validators: CaseRequirementValidationApiModel[];
}

export interface CaseDetailsApiModel {
  History?: CaseHistoryApiModel[];
  Communications?: CaseCommunicationApiModel[];
  Notes?: CaseNoteApiModel[];
  MetaData?: CaseMetaDataApiModel[];
  ClaimStatus?: CaseTypeStatusApiModel;
  StatusTimeToLive?: Date;
  ClaimType?: CaseApplicationTypeApiModel;
  LastUpdated?: Date;
  SubType?: string;
  InternalId?: string;
  CustomerIdentifier?: string;
  CustomerName?: string;
  Created?: Date;
  Id: number;
  JoinedCurrentStatus?: Date;
}

interface ClaimStatusApiModel {
  Name: string;
  Id: string;
}
interface ClaimTypeApiModel {
  Name: string;
  Id: string;
}

export interface GetCustomersResponse {
  Customers: Customer[];
}
export interface UpdateCustomersResponse {
  Customer: Customer;
}
export interface GetDropLocationsResponse {
  DropLocations: DropLocation[];
}

export interface GetDropLocationResponse {
  DropLocation: DropLocation;
}

export interface OpeningTime {
  ClosingTime: string;
  Day: string;
  OpeningTime: string;
}

export interface DropLocation {
  Address1: string;
  Address2: string;
  Code: string;
  County: string;
  Distance: number;
  AcceptsDelivery: boolean;
  Id: number;
  IsLocker: boolean;
  Latitude: number;
  Longitude: number;
  Name: string;
  Postcode: string;
  Rating: number;
  PrintInStoreAvailable: boolean;
  OpeningTimes: OpeningTime[];
}

export interface CreateCustomersResponse {
  Customer: Customer;
}

export interface RestoreItemsResponse {
  Items: SmartSendItemApiModel[];
}

export interface GetQuoteOptionsResponse {
  Quotes: QuoteApiModel[];
}

export interface GetQuoteProvidersResponse {
  Providers: QuoteProviderApiModel[];
}

export interface GetServiceTagsResponse {
  Tags: CustomerServiceTag[];
}

export interface UpdateServiceTagsResponse {}

export interface GetServiceDetailsResponse {
  Service: ServiceDetailsModel;
}

export interface ServiceDetailsModel {
  Id: string;
  AccountId: string;
  Provider: string;
  Name: string;
  DropOffProviderCode: string;
  CourierName: string;
  CollectionType: string;
  DeliveryType: string;
  MaxHeight?: number;
  MaxWidth?: number;
  MaxLength?: number;
  MaxWeight?: number;
  MaxCover?: number;
  IsPrinterRequired: boolean;
  ShortDescriptions: string;
  Features: string;
  RequiresDimensions: boolean;
  Classification: string;
  Tags: string[];
  DeliveryCountries: {
    Iso3Code: string;
    Subdivision: string;
    Name: string;
  }[];
  CollectionCountries: {
    Iso3Code: string;
    Subdivision: string;
    Name: string;
  }[];
}

export interface GetShipmentActionsResponse extends ItemActions {
  TotalPrice?: number;
  TotalVat?: number;
  TotalPriceExVat?: number;
  Reference?: string;
  BookingStatus?: BookingStatusType;
}
export interface GetItemActionsResponse extends ItemActions {}

export interface GetOrderActionsResponse extends ItemActions {}

export interface GetPrintInStoreResponse extends ItemActions {}

export interface GetOrderResponse {
  Order: SmartSendItemApiModel;
}

export interface CreateReturnCollectionResponse {
  Item: SmartSendItemSnapshotApiModel;
}

export interface ItemActions {
  Links: LinkApiModel[];
}

export interface UniQueryResponse {
  result: {
    Payload: [any];
  };
}

export interface GetAccountPaymentOptionsResponse {
  PaymentOptions: PaymentOptionApiModel[];
}

export interface GetPaymentOptionBalanceResponse {
  Balance: number;
}

export interface GetRefundsResponse {
  Refunds: RefundApiModel[];
}

interface RefundApiModel {
  Amount: number;
  Status: string;
  OrderLineId: number;
  Created: Date;
}
export interface GetLabelResponse {
  Labels: string[];
  FailedLabels: number;
  SuccessfulLabels: number;
}
export interface GetOrderTrackingResponse extends TrackingResult {}
export interface GetTrackingResponse extends TrackingResult {}

export interface ParcelTrackingRecord {
  TrackingNumber: string;
  ProviderSource: string;
  Provider: string;
  CurrentStage: string;
  Stages: TrackingStages;
  TrackingEvents: ParcelTrackingEvent[];
}

export interface ParcelTrackingEvent {
  Stage: string;
  Details: string;
  Description: string;
  Timestamp: Date;
  Images: TrackingImageApiModel[];
}

export interface TrackingImageApiModel {
  Name: string;
  Title: string;
  Dara: string;
  Type: 'Src' | 'Base64' | string;
}

export interface TrackingResult {
  Tracking: ParcelTrackingRecord[];
  Trackable: boolean;
  Tags: ('RTS' | 'Trackable' | string)[];
  Links: { [key: string]: string };
  IsDropOffService?: boolean;
  IsShopDelivery?: boolean;
  DropOffProviderGroupName?: string;
}

export interface GetPackagesResponse {
  Packages: PackageApiModel[];
}

export interface GetTagsResponse {
  Tags: string[];
}

export interface ShipResponse {
  Links?: LinkApiModel[];
  TotalPrice?: number;
  TotalVat?: number;
  TotalDiscount?: number;
  DiscountCode?: string;
  TotalPriceExVat?: number;
  Reference?: string;
  ManifestIds?: string;
  BookingStatus?: BookingStatusType;
  Errors?: ValidationErrorApiModel[];
  Items?: ItemPriceSummaryApiModel[];
  Tags?: string[];
}

export interface VerifyShipResponse extends ShipResponse {
  Breakdown?: Record<string, number>;
}

export interface ItemPriceSummaryApiModel {
  Price: number;
  Vat: number;
  PriceExVat: number;
  Discount: number;
  ItemId: string;
}

export interface LinkApiModel {
  Label: string;
  Type: string;
  Href: string;
}

export interface ValidationErrorApiModel {
  Type: string;
  Error: string;
  ItemId: string;
}

export type BookingStatusType = 'None' | 'Booked' | 'Staged' | 'Failed' | 'Prepared';

export interface GetQuoteValidationResponse extends QuoteState {}
export interface GetBulkQuoteValidationResponse {
  Quotes: { [key: string]: QuoteState };
}
export interface GetBulkProhibRequest {
  Items: ProhibRequestItem[];
}

export interface ProhibRequestItem {
  Id: string;
  Description: string;
  SenderCountryIsoCode: string;
  RecipientCountryIsoCode: string;
  Service: string;
  Provider: string;
  AccountId: string;
}

export interface GetBulkProhibResponse {
  Validation: ItemRestriction[];
}

export interface ItemRestriction {
  Id: string;
  Restriction: string;
  Description: string;
  Error: string;
}

export interface GetDeliveredDutyPaidResponse {
  DutyValue: number;
  VatValue: number;
  AdminFee?: number;
  Calculations: DeliveryDutyPaidCalculation[];
  Breadkdown?: Record<string, number>;
}

export interface DeliveryDutyPaidCalculation {
  Id: string;
  Description: string;
  HSCode: string;
  DutyValue: number;
  VatValue: number;
  AdminFee?: number;
}

export interface MarketplaceDispatchItemDetailsModel {
  OverallDispatchStatus: string;
  MarketplaceOrderlineDispatches: MarketplaceOrderlineDetailsModel[];
}

export interface MarketplaceOrderlineDetailsModel {
  Id: string;
  ParentImportedItemId: string;
  DispatchStatus: string;
  IsAutomaticDispatch: boolean | null;
  StatusUpdatedAt: Date | null;
  AutoDispatchAfter: Date | null;
  MaxAttempts: number | null;
  CurrentAttempt: null;
  RetryAfter: Date | null;
}

export interface SmartSendSettingsApiResponseModel {
  SmartSend: { [key: string]: string };
  Icons: { [key: string]: string };
  Links: { [key: string]: string };
  Limits: { [key: string]: number };
  Downloads: { [key: string]: string };
  Features: Feature[];
  Help?: HelpSection[];
  LiveChatConfiguration?: LiveChatConfiguration;
  MarketplaceCourierLimits: { [key: string]: Set<string> };
  Metadata: { [key: string]: string };
}

export interface LiveChatConfiguration {
  AccountId?: string;
  WebsiteId?: string;
  ButtonId?: string;
  Provider?: string;
}

export type HelpSection = 'HavingIssues';

export interface SearchAddressesApiResponseModel {
  ResultsCount: number;
  Skip: number;
  Take: number;
  Results: AddressSearchApiModel[];
}
export interface GetAddressBreakdownApiResponseModel {
  ResultsCount: number;
  Skip: number;
  Take: number;
  Results: AddressSearchApiModel[];
}
export interface GetAddressFullDetailsApiResponseModel {
  Result: Customer;
}
export interface AddressSearchApiModel {
  CountryIsoCode?: string;
  Description?: string;
  Display?: string;
  HasBreakdown?: boolean;
  Highlight?: string;
  Identifier?: string;
  LastSearchTerm?: string;
  Property?: string;
}

export interface UpdateItemResponse extends SmartSendItemApiModel {}
export interface BulkUpdateItemsResponse {
  Items: SmartSendItemApiModel[];
}

export interface CreateItemResponse {
  Items: ItemCreateResult;
  Message?: string;
}

export interface ItemCreateRequest {
  [key: string]: SmartSendItemApiModel;
}
export interface ItemCreateResult {
  [key: string]: ItemCreateResultModel;
}

export interface ItemCreateResultModel {
  Id: string;
  Error: string;
  Success: boolean;
}

export interface GroupItemsResponse {
  AddedItems: SmartSendItemApiModel[];
  ModifiedItems: SmartSendItemApiModel[];
  DeletedItems: string[];
}

export interface QuoteProviderApiModel {
  Id: string;
  Name: string;
  GroupName: string;
  Manifestable: boolean;
  SuggestFilteredView: boolean;
  Accounts: QuoteProviderAccountApiModel[];
}

export interface CollectionDateApiModel {
  Date: Date;
  Breakdown?: Record<string, number>;
}

export interface InputRuleApiModel {
  helperText?: string;
  label: string;
  max?: number;
  maxLength?: number;
  min?: number;
  minLength?: number;
  name: string;
  value: string;
  pattern?: string;
  dataRequiredIf?: string | RequiredIfModel | null;
  required: boolean;
  /**
   * `'boolean'` is the quoting API's own valueType, forwarded verbatim rather than translated to an
   * HTML input type - it renders as a checkbox, not an `<input type>`. Everything else here is a real
   * HTML input type.
   */
  type?: 'text' | 'email' | 'number' | 'tel' | 'url' | 'search' | 'submit' | 'boolean';
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  /**
   * On a boolean rule, the key of the extra its checkbox drives: while the box is ticked that extra is
   * selected on the item, while it is unticked it is removed. Nothing reads a specific extra key, so any
   * future rule carrying this works without a code change.
   */
  appliesExtra?: string;
  /**
   * Server-supplied default for a boolean rule, as a string (`'true'` / `'false'`). Used to seed the
   * checkbox when the address carries no stored value yet - never hardcode the default, since the
   * polarity is the courier's to decide.
   */
  default?: string;
}

export type RequiredIfCondition = {
  field: string;
  value: string | string[] | null;
  compare?: 'Eq' | 'NotEq';
};

// Entries within "all"/"any" may omit their own value, instead sharing the group's top-level value.
export type RequiredIfGroupCondition = Omit<RequiredIfCondition, 'value'> & { value?: RequiredIfCondition['value'] };

export type RequiredIfModel = Partial<RequiredIfCondition> & {
  all?: RequiredIfGroupCondition[];
  any?: RequiredIfGroupCondition[];
};

export interface QuoteApiModel {
  Service: QuoteServiceApiModel;
  ClosestPickupLocation?: DropLocation;
  Options: QuoteOptionApiModel[];
  CurrencyCode: string;
  Tags?: string[];
}

export interface PackageApiModel {
  Provider: string;
  Id: string;
  Name: string;
  Thumb: string;
  Length: number;
  Width: number;
  Height: number;
  Weight: number;
}

export interface CustomerServiceTag {
  ServiceSlug: string;
  GroupIdentifier: string;
  Tags: string[];
}

export interface QuoteOptionApiModel extends QuoteOptionBaseApiModel {
  TotalPrice: number;
  TotalPriceExVat: number;
  TotalVat: number;
  VatRate: number;
  Collection: Date;
  CutOff: Date;
  EstimatedDeliveryDate?: Date;
  EstimatedDeliveryDateMin?: Date;
  AvailableExtras: QuoteExtraApiModel[];
  ManualCalculations?: QuoteExtraApiModel[];
}

export interface QuoteOptionBaseApiModel {
  CoverCost: number;
  Covered: number;
  CoverPercentage: number;
  Key?: string;
  Extras: QuoteExtraApiModel[];
  Unavailable?: boolean;
  CoverCostBreakdown?: Record<string, number>;
}

export interface QuoteServiceApiModel extends Service {
  DropOffProviderCode?: string;
  PickUpProviderCode?: string;
  CourierName: string;
  CourierId: string;
  CollectionType: string;
  CourierProductName?: string;
  CourierProductCode: string;
  DeliveryType: string;
  MaxHeight?: number;
  MaxWidth?: number;
  MaxLength?: number;
  MaxWeight?: number;
  MaxCover: number;
  PrinterRequired: boolean;
  RequiresManifest: boolean;
  DimensionsRequired: boolean;
  Tags: string[];
  TariffSystem?: string;
  Links?: LinkApiModel[];
  FirstMileCourierSlug?: string;
  FirstMileCourierName?: string;
  LastMileCourierSlug?: string;
  LastMileCourierName?: string;
}

export interface QuoteExtraApiModel extends ExtraBase {
  Price: number;
  Vat: number;
  Duties?: number;
  AdminFee?: number;
  Total: number;
  /**
   * Free-form extra metadata forwarded from the quoting API. The edge already serialises this on
   * AvailableExtras (its `QuoteExtra.Details`) - every value is flattened to a string on the way
   * through, so read booleans as `'true'`/`'false'`.
   *
   * Note it is NOT populated on the selected `Extras` branch of a quote option, so once an extra is
   * ticked its metadata is absent. Read `pricePending` via IsExtraPricePending rather than reaching
   * in here directly.
   */
  Details?: Record<string, string>;
}

export interface QuoteProviderAccountApiModel {
  Id: string;
  Name: string;
  Provider: string;
}

export interface PaymentOptionApiModel {
  AccountId: string;
  Provider: string;
  Balance: number;
  CurrencyCode: string;
  Type: PaymentOptionType;
  Options: PaymentOptionOptionApiModel[];
}

export interface PaymentOptionOptionApiModel {
  Id: string;
  Display: string;
}

export type PaymentOptionType = 'Invoiced' | 'TopUp' | 'SavedPayment' | 'External' | 'CourierAccount';

export interface ConsignmentParcelApiModel {
  Contents: ConsignmentParcelItemApiModel[];
  Dimensions: Dimensions;
  Id: string;
  UID: string;
  CanAdd: boolean;
}

export interface ConsignmentParcelItemApiModel {
  Id: string;
  UID: string;
  Value: number;
  Quantity: number;
  Description: string;
}

export interface ConsignmentApiModel {
  Parcels: ConsignmentParcelApiModel[];
}

export type ManifestCanCompleteType =
  'MANIFESTABLE' | 'CUTOFF-MISSED' | 'COMPLETED' | 'PROCESSING' | 'FUTURE' | 'PAST' | 'NOITEMS';

export interface SmartSendItemApiModel {
  Type?: string;
  SubType?: string;
  ManifestStatus?: ManifestCanCompleteType;
  Id: string;
  CustomerId: string;
  Description: string;
  MarketplaceStatus: string;
  Status: string;
  PackageDimensions: Dimensions;
  Source: MarketplaceSource;
  Recipient: Customer;
  Sender: Customer;
  Extras: ExtraBase[];
  Service: Service;
  Meta: Meta;
  Tags: string[];
  Owners: Owner[];
  Notes: [];
  Created: Date;
  MarketplaceTransactionDate?: Date;
  ReferenceSummary?: string;
  PreferredCollectionDate?: Date;
  CreatedBy?: string;
  TrackingSummary?: TrackingSummaryApiModel;
  /** Coarse tracking status from the Algolia search hit ('New' means no scans yet). Distinct from TrackingSummary.LatestStatus. */
  SearchTrackingStatus?: string;
  /** From the Algolia search hit's service - true for drop-off services, false for collections. */
  SearchIsDropOffService?: boolean;
  Updated: Date;
  UpdatedBy?: string;
  MarketplaceOrderLines: MarketplaceOrderApiModel[];
  Value: number;
  CurrencyCode: string;
  PropertyKey: string;
  BookingDetails: BookingDetails;
  State?: SmartSendItemState;
  Consignment?: ConsignmentApiModel;
  Statuses?: KeyStatuses;
}

export const KnownTrackingStatuses = ['Created', 'Delivered', 'New', 'InTransit', 'Collected', 'DroppedOff'] as const;

// Values based on KnownTrackingStatuses
export type TrackingStatus = (typeof KnownTrackingStatuses)[number];

export interface KeyStatuses {
  ClaimStatus?: string;
  RefundStatus?: string;
  TrackingStatus?: TrackingStatus;
}

export interface TrackingSummaryApiModel {
  LatestStatus: string;
  DroppedOff?: Date;
  Collected?: Date;
  InTransit?: Date;
  AtDepot?: Date;
  DeliveryScheduled?: Date;
  Delivered?: Date;
  MajorError?: Date;
  LastUpdated?: Date;
}
export interface Service {
  Id: string;
  Name: string;
  Provider: string;
  AccountId: string;
  Meta: Meta;
  Tags?: string[];
}
export interface BookingDetails {
  BookingStatus: 'None' | 'Processing' | 'AwaitingPayment' | 'Booked' | 'Cancelled';
  Error?: any;
  TrackingUrl?: string;
  ParcelNumbers?: Meta;
  Reference?: string;
  OrderId?: string;
  OrderLineId?: string;
  Service?: Service;
  EstimatedCollectionDate?: Date;
  EstimatedDeliveryDate?: Date;
  Created: Date;
  CreatedBy?: string;
  FinancialSummary?: FinancialSummary;
}
export interface FinancialSummary {
  Vat: number;
  Net: number;
  Total: number;
  CurrencyCode: string;
  Meta: Meta;
}
export interface MarketplaceOrderApiModel {
  Id: string;
  CustomerReference?: string;
  InternalReference?: string;
  MarketplaceOrderId: string;
  MarketplaceOrderLineId: string;
  Source: MarketplaceSource;
  Product: Product;
  OrderLink: string;
  Value: number;
  CurrencyCode: string;
  Quantity: number;
  Meta: Meta;
  MarketplaceStatus: string | number;
  Status: string | number;
  Packaged: boolean;
  Created: Date;
  CreatedBy: string;
}
export interface Owner {
  Id: string;
  Name: string;
}
export interface Product {
  Id?: string;
  Dimensions?: Dimensions;
  ProductLink?: string;
  Description?: string;
  Sku?: string;
  ImageUrl?: string;
  TariffCode?: string;
  CountryOfManufacture?: string;
  Meta: Meta;
}
export interface ExtraBase {
  Id: string;
  Name: string;
  Provider?: string;
}
export interface Dimensions {
  Height?: number;
  Width?: number;
  Length?: number;
  Weight?: number;
}
export interface MarketplaceSource {
  Id: string;
  Name: string;
  AccountId: string;
}
export interface Country {
  CountryIsoCode: string;
  CountryIso2Code: string;
  Name: string;
  LocalisedName?: string;
  CountrySubdivision: string;
  States: { Name: string; Code: string }[];
  Switches: CountrySwitch[];
}

export interface CountrySwitch {
  FromIsoCode: string;
  FromSubdivision: string;
  ToIsoCode: string;
  ToSubdivision: string;
  Pattern: string;
}

export interface Customer {
  Id?: string;
  VatStatus?: string;
  VatNumber?: string;
  CountrySubdivision?: string;
  CountryIsoCode: string;
  LegacyCountryId?: number;
  Postcode?: string;
  County?: string;
  Town?: string;
  Locality?: string;
  AddressLine1?: string;
  AddressLine2?: string;
  Phone?: string;
  Email?: string;
  Organisation?: string;
  Name?: string;
  Provider: string;
  AccountName?: string;
  PropertyKey?: string;
  Meta: Meta;
}

export class Meta {
  [key: string]: MetaValue;
}

export interface MetaValue {
  Value: string;
  Type: string;
}

export class TrackingStages {
  [key: string]: Date;
}

export interface QuoteState {
  Quote: QuoteApiModel;
  Dates: CollectionDateApiModel[];
  SenderValidation: InputRuleApiModel[];
  RecipientValidation: InputRuleApiModel[];
  ItemValidation: InputRuleApiModel[];
  ProductValidation: InputRuleApiModel[];
  Restrictions?: ItemRestriction[];
  Locations?: {
    PickupLocation?: DropLocation;
    DropOffLocation?: DropLocation;
  };
  Tags: string[];
}

export interface Validation {
  QuoteState?: QuoteState;
  Validated: boolean;
  Errors: ValidationType[];
  Warnings: string[];
}

export type ValidationType =
  | 'SERVICE-UNAVAILABLE'
  | 'SERVICE-MISSING'
  | 'ITEM-WEIGHT'
  | 'ITEM-DIMENSIONS'
  | 'ITEM-PROHIB'
  | 'ADDRESS-RECIPIENT'
  | 'ADDRESS-SENDER'
  | 'QUOTE'
  | string;

export interface MarketplaceImportTask {}

export interface MarketplaceProviderApiModel {
  Providers: MarketplaceIntegrationApiModel[];
}
export interface TriggerAllImportsApiModel {
  Providers: MarketplaceIntegrationApiModel[];
}
export interface GetCountriesResponse {
  Countries: Country[];
}

export interface MarketplaceIntegrationApiModel {
  Id: string;
  Name: string;
  Links: { [key: string]: string };
  Accounts: MarketplaceIntegrationAccountApiModel[];
}

export interface MarketplaceIntegrationAccountApiModel {
  Created: Date;
  Expired: boolean;
  SetupRequired: boolean;
  Id: string;
  Name: string;
  Links: { [key: string]: string };
}

export interface TariffCodeSuggestionApiResponse {
  Suggestions: TariffCodeSuggestion[];
}

export interface TariffCodeSuggestion {
  Name: string;
  CN?: string;
  TARIC?: string;
  AHECC: string;
  HS: string;
  Categories: TariffCodeCategory[];
}

export interface TariffCodeCategory {
  Name: string;
  Meta: TariffCodeMetaData;
}

export interface TariffCodeMetaData {
  ProtectionLevel: boolean;
  Prohibited: boolean;
  Tariffs: TariffCodeTariff;
}

export interface TariffCodeTariff {
  HS: string;
  CN: string;
  TARIC?: string;
  AHECC: string;
}
